import { Controller } from "@hotwired/stimulus"

const DRAG_CLASS = "opacity-50"
const ITEM_DATA_TYPE = "x-origin/item"
const REORDERABLE_QUERY_SELECTOR = "[data-controller~='reorderable']"

export default class ReorderableController extends Controller {
  static targets = ["item", "ordinalInput"]

  connect() {
    this.originalZIndex = this.element.style.zIndex
    this.element.addEventListener("drop", this.drop.bind(this))

    this.boundDragover = this.dragover.bind(this)
  }

  itemTargetConnected(itemElement) {
    this.#addDragListeners(itemElement)

    if (!itemElement.hasAttribute("draggable")) {
      itemElement.setAttribute("draggable", true)
    }

    this.#updateItemIndices()
  }

  dragstart(event) {
    document.addEventListener("dragover", this.boundDragover)

    this.dispatch("dragstart")
    this.draggedItem = event.target
    this.draggedItem.style.zIndex = 1
    event.dataTransfer.setData(ITEM_DATA_TYPE, this.draggedItem.dataset.index)
    this.element.setAttribute("data-dragging", true)
    this.originalIndex = parseInt(this.draggedItem.dataset.index)
    this.originalPosition = this.#getItemPosition(this.draggedItem)

    this.#addDraggedItemOpacity()
  }

  dragover(event) {
    event.preventDefault()

    if (this.#isWithinParentBounds(event)) {
      this.#updateItemPositions(event)
    } else {
      this.#restoreOriginalPosition()
    }
  }

  dragenter(event) {
    event.preventDefault()
  }

  dragleave(event) {
    event.preventDefault()
  }

  dragend() {
    document.removeEventListener("dragover", this.boundDragover)
    this.dispatch("dragend")
    this.#resetDragState()
  }

  drop(event) {
    event.preventDefault()
    if (event.dataTransfer.types.includes(ITEM_DATA_TYPE)) {
      this.#handleReorder()
    }
    this.#resetDragState()
  }

  #handleReorder() {
    if (!this.draggedItem) return

    const newIndex = Array.from(this.itemTargets).indexOf(this.draggedItem)
    const oldIndex = this.originalIndex

    if (newIndex !== -1 && newIndex !== oldIndex) {
      this.#dispatchReorderEvent(oldIndex, newIndex)
    } else {
      this.#restoreOriginalPosition()
    }

    this.#updateItemIndices()
  }

  #resetDragState() {
    if (this.draggedItem) {
      this.draggedItem.classList.remove(DRAG_CLASS)
      this.draggedItem.style.zIndex = this.originalZIndex
    }
    this.element.removeAttribute("data-dragging")
    delete this.originalIndex
    delete this.originalPosition
    this.draggedItem = null
  }

  #addDragListeners(element) {
    element.addEventListener("dragstart", this.dragstart.bind(this))
    element.addEventListener("dragenter", this.dragenter.bind(this))
    element.addEventListener("dragleave", this.dragleave.bind(this))
    element.addEventListener("dragend", this.dragend.bind(this))
  }

  #addDraggedItemOpacity() {
    requestAnimationFrame(() => {
      this.draggedItem.classList.add(DRAG_CLASS)
    })
  }

  #updateItemPositions(event) {
    const itemUnderCursor = event.target.closest(
      "[data-reorderable-target='item']"
    )

    if (!this.draggedItem || itemUnderCursor == this.draggedItem) return

    if (itemUnderCursor && this.#isWithinSameParentScope(itemUnderCursor)) {
      const itemRect = itemUnderCursor.getBoundingClientRect()
      const midX = itemRect.left + itemRect.width / 2
      const midY = itemRect.top + itemRect.height / 2

      const draggedItemIndex = Array.from(
        itemUnderCursor.parentNode.children
      ).indexOf(this.draggedItem)
      const itemUnderCursorIndex = Array.from(
        itemUnderCursor.parentNode.children
      ).indexOf(itemUnderCursor)

      if (
        event.clientX < midX &&
        event.clientY < midY &&
        draggedItemIndex + 1 !== itemUnderCursorIndex
      ) {
        itemUnderCursor.parentNode.insertBefore(
          this.draggedItem,
          itemUnderCursor
        )
      } else if (draggedItemIndex - 1 !== itemUnderCursorIndex) {
        itemUnderCursor.parentNode.insertBefore(
          this.draggedItem,
          itemUnderCursor.nextSibling
        )
      }
    } else if (!itemUnderCursor && this.#isWithinParentBounds(event)) {
      const containerRect = this.element.getBoundingClientRect()
      const draggedItemRect = this.draggedItem.getBoundingClientRect()

      const draggedItemIndex = Array.from(
        this.draggedItem.parentNode.children
      ).indexOf(this.draggedItem)

      if (
        event.clientY < containerRect.top + draggedItemRect.height / 2 &&
        draggedItemIndex !== 0
      ) {
        this.element.insertBefore(this.draggedItem, this.element.firstChild)
      } else if (
        event.clientY > containerRect.bottom - draggedItemRect.height / 2 &&
        draggedItemIndex !== this.draggedItem.parentNode.children.length - 1
      ) {
        this.element.appendChild(this.draggedItem)
      }
    }
  }

  #isWithinSameParentScope(item) {
    return (
      item.closest(REORDERABLE_QUERY_SELECTOR) === this.element &&
      this.draggedItem.closest(REORDERABLE_QUERY_SELECTOR) === this.element
    )
  }

  #isWithinParentBounds(event) {
    const containerRect = this.element.getBoundingClientRect()

    return (
      event.clientX >= containerRect.left &&
      event.clientX <= containerRect.right &&
      event.clientY >= containerRect.top &&
      event.clientY <= containerRect.bottom
    )
  }

  #dispatchReorderEvent(oldIndex, newIndex) {
    this.dispatch("reordered", {
      target: this.draggedItem,
      bubble: true,
      detail: {
        value: {
          values: {
            from: oldIndex + 1,
            to: newIndex + 1,
          },
        },
      },
    })
  }

  #updateItemIndices() {
    this.itemTargets.forEach((item, index) => {
      item.dataset.index = index.toString()

      const ordinalInput = this.ordinalInputTargets.find((ordinalInputTarget) =>
        item.contains(ordinalInputTarget)
      )
      if (ordinalInput) {
        ordinalInput.value = index
      }
    })
  }

  #getItemPosition(item) {
    return Array.from(this.element.children).indexOf(item)
  }

  #restoreOriginalPosition() {
    const currentPosition = this.#getItemPosition(this.draggedItem)

    if (currentPosition !== this.originalPosition) {
      const positionIncreased = currentPosition > this.originalPosition
      const referenceNode = this.element.children[this.originalPosition]
      referenceNode.insertAdjacentElement(
        positionIncreased ? "beforebegin" : "afterend",
        this.draggedItem
      )
    }
  }
}
